import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconPaperclip, IconX } from '@tabler/icons-react';
import validate from '@/helpers/validations';
import { getMutationResult } from '@/helpers/query';
import { useCreateLeadMutation } from '@/services/api';
import OptionCardGroup from '../OptionCardGroup';
import classes from './style.module.css';

const SERVICE_OPTIONS = [
  {
    value: 'new',
    title: 'משכנתא חדשה',
    desc: 'בדיקת כדאיות למשכנתא חדשה שאתם שוקלים',
  },
  {
    value: 'refinance',
    title: 'מחזור משכנתא קיימת',
    desc: 'בחינת כדאיות למחזור המשכנתא הקיימת שלכם',
  },
];

const GUIDANCE_OPTIONS = [
  {
    value: 'call',
    title: 'אשמח להתייעץ בשיחה',
    desc: 'שיחה ראשונית להתרשמות',
  },
  {
    value: 'focused',
    title: 'ליווי ממוקד ושיחת ייעוץ',
    desc: 'שיחת ייעוץ ממוקדת לנקודות המפתח',
  },
  {
    value: 'full',
    title: 'ליווי אישי מלא',
    desc: 'ליווי צמוד לאורך כל התהליך',
  },
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];

const LeadForm = ({ onSuccess, agentId }) => {
  const [createLead] = useCreateLeadMutation();
  const [loading, setLoading] = useState(false);
  const [serviceType, setServiceType] = useState('new');
  const [guidanceType, setGuidanceType] = useState('call');
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      fullName: '',
      mobileNumber: '',
      email: '',
    },
    validate: {
      fullName: (value) => validate(value, { required: 'נא להזין שם מלא' }),
      mobileNumber: (value) => validate(value, {
        required: 'נא להזין מספר טלפון',
        mobile: 'מספר טלפון לא תקין',
      }),
      email: (value) => validate(value, {
        required: 'נא להזין דואר אלקטרוני',
        email: 'דואר אלקטרוני לא תקין',
      }),
    },
  });

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] ?? null;
    if (!selected) {
      setFile(null);
      return;
    }

    const extension = selected.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      notifications.show({
        title: 'סוג קובץ לא נתמך',
        message: 'ניתן לצרף קבצי PDF, Word או Excel בלבד.',
        color: 'red',
      });
      clearFile();
      return;
    }

    if (selected.size > MAX_FILE_SIZE) {
      notifications.show({
        title: 'הקובץ גדול מדי',
        message: 'ניתן לצרף קובץ בגודל של עד 5MB.',
        color: 'red',
      });
      clearFile();
      return;
    }

    setFile(selected);
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    const formData = new FormData();
    formData.append('fullName', values.fullName);
    formData.append('mobileNumber', values.mobileNumber);
    formData.append('email', values.email);
    formData.append('serviceType', serviceType);
    formData.append('guidanceType', guidanceType);
    if (agentId) {
      formData.append('agentId', agentId);
    }
    if (serviceType === 'refinance' && file) {
      formData.append('file', file);
    }

    const { isError } = await getMutationResult(formData, createLead);

    setLoading(false);

    if (isError) {
      notifications.show({
        title: 'שליחת הטופס נכשלה',
        message: 'אירעה שגיאה בשליחת הפרטים. נסו שוב מאוחר יותר.',
        color: 'red',
      });
      return;
    }

    onSuccess();
  };

  return (
    <form className={classes.form} onSubmit={form.onSubmit(handleSubmit)} noValidate>
      <div className={classes.row}>
        <TextInput
          className={classes.field}
          classNames={{ input: classes.input, label: classes.label }}
          label="שם מלא"
          placeholder="הזינו שם מלא"
          withAsterisk
          key={form.key('fullName')}
          {...form.getInputProps('fullName')}
        />
        <TextInput
          className={classes.field}
          classNames={{ input: classes.input, label: classes.label }}
          label="טלפון נייד"
          placeholder="הזינו מספר טלפון"
          maxLength={10}
          withAsterisk
          key={form.key('mobileNumber')}
          {...form.getInputProps('mobileNumber')}
        />
      </div>

      <TextInput
        classNames={{ input: classes.input, label: classes.label }}
        label="דואר אלקטרוני"
        placeholder="name@example.com"
        withAsterisk
        key={form.key('email')}
        {...form.getInputProps('email')}
      />

      <OptionCardGroup
        label="סוג השירות המבוקש"
        options={SERVICE_OPTIONS}
        value={serviceType}
        onChange={setServiceType}
        columns={2}
      />

      {serviceType === 'refinance' && (
        <div className={classes.upload}>
          <button
            type="button"
            className={classes.uploadBox}
            onClick={() => fileRef.current?.click()}
          >
            <IconPaperclip size={18} className={classes.uploadIcon} />
            <span className={classes.uploadText}>
              {file ? file.name : 'מומלץ לצרף את דוח יתרות המשכנתא שלכם'}
            </span>
            {file && (
              <IconX
                size={16}
                className={classes.uploadClear}
                onClick={(event) => {
                  event.stopPropagation();
                  clearFile();
                }}
              />
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            hidden
            onChange={handleFileChange}
          />
          <span className={classes.uploadHint}>קבצי PDF, Word או Excel בלבד, עד 5MB</span>
          <span className={classes.uploadHint}>
            <a
              href="https://app.creditpro.co.il/docs/מחזור-הנחיות%20דוח%20יתרות%20לסילוק%20קרדיט%20פרו.pdf"
              target="_blank"
              rel="noreferrer"
            >
              לחץ לקריאת הנחיות לקבלת דוח יתרות מהבנק
            </a>
          </span>
        </div>
      )}

      <OptionCardGroup
        label="סוג הליווי המבוקש"
        options={GUIDANCE_OPTIONS}
        value={guidanceType}
        onChange={setGuidanceType}
        columns={3}
      />

      <Button
        className={classes.submit}
        type="submit"
        color='#16345e'
        radius="md"
        size="lg"
        loading={loading}
        rightSection={<IconArrowLeft size={20} />}
        fullWidth
      >
        שלח בדיקה ללא עלות
      </Button>

      <p className={classes.privacy}>
        שירות זה מוצע בשיתוף Credit Pro
        <br />
        הפרטיות שלכם חשובה לנו — המידע שלכם מאובטח ולא יועבר לצד שלישי.
        <br />
        מילוי פרטים בדף הנחיתה מהווה את הסכמתך להעברת
        המידע הנדרש לחברת הייעוץ לצורך ביצוע הבדיקה ללא עלות.
      </p>
    </form>
  );
};

LeadForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  agentId: PropTypes.string,
};

export default LeadForm;
